import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const BOOKING_TERMS = {
  intro:
    "Please read these Terms & Conditions before confirming your booking. MoversNearby provides moving services and dispatches movers to carry out the booked move. By proceeding with a booking, you acknowledge that you have read and understood these terms, subject to any rights that cannot lawfully be excluded under Australian law.",
  sections: [
    {
      h: "1. Booking Fee & Payment",
      items: [
        "A booking fee is payable to secure your moving booking.",
        "If you cancel within 48 hours of making your original booking, the booking fee will be refunded in full.",
        "If you cancel after 48 hours from the original booking, the booking fee is non-refundable.",
        "If MoversNearby is unable to provide the booked moving service or cancels the booking, the booking fee will be refunded in full.",
        "Any additional charges applicable to your move will be communicated where possible and must be paid in accordance with the agreed payment terms.",
      ],
    },
    {
      h: "2. Access to the Property & Moving Location",
      items: [
        "The customer must ensure that MoversNearby's movers have safe, clear and reasonable access to both the pickup and delivery locations.",
        "The customer is responsible for arranging suitable parking, building access, lifts, permits and any necessary permissions before the scheduled booking time.",
        "The customer or an authorised representative must be present at the property to provide access and instructions.",
        "If movers cannot access the property, or access is delayed or restricted, additional charges may apply for waiting time, redelivery, storage or other services required.",
        "Please inform MoversNearby in advance about stairs, narrow hallways, long carrying distances, lift restrictions, difficult access, heavy items or any other circumstances that may affect the move.",
      ],
    },
    {
      h: "3. Packing & Preparation",
      items: [
        "Unless packing services have been specifically included in your booking, the customer is responsible for packing belongings securely before the movers arrive.",
        "Fragile, valuable and personal items should be packed appropriately and clearly labelled.",
        "The customer must inform MoversNearby in advance about pianos, pool tables, unusually heavy items, oversized furniture or items requiring special handling.",
        "Additional charges may apply if the actual job differs from the information provided when the booking was made.",
      ],
    },
    {
      h: "4. Insurance & Insurance Excess",
      items: [
        "The customer should check that their belongings have adequate insurance coverage for the move.",
        "Transit insurance may be available for an additional premium, subject to the applicable insurance policy, eligibility requirements, exclusions and limits.",
        "Where arranged and confirmed, cover of up to $100,000 per customer per move may apply, with an excess of $500 per claim, subject to the policy terms.",
        "Insurance excess is separate from the insurance premium. If a claim is accepted and an excess is payable under the applicable policy, the customer is responsible for paying the applicable excess separately.",
        "Insurance coverage is not automatic unless it has been arranged and confirmed. Customers should review the applicable policy before relying on any cover.",
        "Any damage, loss or service-related complaint should be reported in writing as soon as possible, and ideally within 7 days, using MoversNearby's official contact details.",
        "Customers should retain photographs, receipts and other relevant evidence to support any claim.",
      ],
    },
    {
      h: "5. Additional Charges",
      items: [
        "No access or waiting time: charges may apply where movers cannot gain access or are delayed due to circumstances outside MoversNearby's control.",
        "Redelivery: charges may apply where delivery cannot be completed as scheduled and another attempt is required.",
        "Tolls: applicable toll charges may be payable, except where an agreed fixed-fee arrangement specifically includes them.",
        "Storage: charges may apply where belongings must be stored because delivery cannot be completed or storage is required.",
        "Special items: additional handling charges may apply for pianos, pool tables or other items requiring special equipment or expertise.",
        "Stairs or difficult access: an access-related charge may apply where relevant, subject to the agreed quote and service terms.",
      ],
    },
    {
      h: "6. Customer Responsibilities",
      items: [
        "The customer must provide accurate information about the inventory, addresses, access conditions and any special handling requirements.",
        "The customer must ensure that items are ready for collection at the agreed time, unless packing or preparation has been included in the booking.",
        "The customer should check the property and inventory at the end of the move and promptly report any apparent issue in writing.",
      ],
    },
    {
      h: "7. MoversNearby Moving Service",
      items: [
        "MoversNearby provides the moving service and dispatches movers to carry out the booked work.",
        "MoversNearby will provide the service described in the booking confirmation, subject to the agreed scope, access conditions, availability and applicable law.",
        "Customers should contact MoversNearby directly using the contact details in their booking confirmation regarding booking changes, service issues, complaints or claims.",
      ],
    },
    {
      h: "8. Changes, Delays & Cancellation",
      items: [
        "The customer should notify MoversNearby as early as possible of any changes to the booking date, addresses, inventory or access arrangements.",
        "Changes may affect availability, pricing and the services that can be provided.",
        "Cancellation and refund entitlements are subject to Section 1, the booking confirmation and any rights that cannot lawfully be excluded under applicable consumer law.",
      ],
    },
    {
      h: "9. Complaints & Applicable Law",
      items: [
        "Please submit complaints and claims in writing using the official contact details provided by MoversNearby.",
        "These terms are intended to operate subject to applicable Australian law, including any consumer rights that cannot lawfully be excluded, restricted or modified.",
        "Nothing in these terms is intended to exclude or limit any non-excludable rights or remedies available to the customer under applicable law.",
      ],
    },
  ],
};

const PAGES = {
  privacy: {
    title: "Privacy Policy",
    testid: "privacy-page",
    body: [
      "MoversNearby respects your privacy. We collect only the information needed to provide our moving services — such as your name, contact details, pickup and delivery addresses, and moving requirements.",
      "We use this information to prepare quotes, confirm bookings, contact you about your move, and improve our services. We do not sell or share your personal information with third parties except where required to deliver your service (for example, payment or communication providers).",
      "You may request access to, or deletion of, your personal information at any time by contacting us at info@moversnearby.com.au or 03 9113 6005.",
    ],
  },
  terms: {
    title: "Booking Terms & Conditions",
    testid: "terms-page",
    terms: BOOKING_TERMS,
  },
};

export default function LegalPage({ page }) {
  const p = PAGES[page];
  return (
    <main className="min-h-screen bg-ink">
      <Header />
      <section className="mx-auto max-w-3xl px-5 pb-24 pt-40" data-testid={p.testid}>
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-blaze">MoversNearby</p>
        <h1 className="font-display text-4xl font-semibold uppercase tracking-tighter text-slate-900 sm:text-5xl">
          {p.title}
        </h1>

        {p.terms ? (
          <div className="mt-8 space-y-8" data-testid="terms-content">
            <p className="text-base leading-relaxed text-slate-600">{p.terms.intro}</p>
            {p.terms.sections.map((s, i) => (
              <div key={i} data-testid={`terms-section-${i}`}>
                <h2 className="font-display text-lg font-bold text-slate-900">{s.h}</h2>
                <ul className="mt-3 space-y-2">
                  {s.items.map((it, j) => (
                    <li key={j} className="flex gap-3 text-[15px] leading-relaxed text-slate-600">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blaze" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-8 space-y-5">
            {p.body.map((para, i) => (
              <p key={i} className="text-base leading-relaxed text-slate-600">{para}</p>
            ))}
          </div>
        )}

        <p className="mt-10 text-sm text-slate-500">
          Questions? Call <a href="tel:0391136005" className="font-semibold text-blaze hover:underline">03 9113 6005</a> or email{" "}
          <a href="mailto:info@moversnearby.com.au" className="font-semibold text-blaze hover:underline">info@moversnearby.com.au</a>
        </p>
      </section>
      <Footer />
    </main>
  );
}
